import { API_BASE, ApiError, getCurrentBranchId, request } from './http'

export type ReaderExportFormat = 'epub3' | 'txt' | 'md' | 'fdx' | 'fountain' | 'pdf' | 'bilingual-md' | 'bilingual-docx'

export interface ReaderModalities {
  text: boolean
  image: boolean
  audio: boolean
  video: boolean
}

export interface ReaderProject {
  id?: string
  title: string
  content_language: string
}

export interface ReaderBranchItem {
  id: string
  name: string
  is_active: boolean
}

export interface ReaderBranch {
  current: string
  available: ReaderBranchItem[]
}

export interface ReaderTocChapter {
  chapter_no: number
  title: string
  status: string
  word_count: number
  modalities: ReaderModalities
}

export interface ReaderToc {
  project: ReaderProject
  branch: ReaderBranch
  chapters: ReaderTocChapter[]
}

export interface ReaderChapter {
  id?: string
  chapter_no: number
  title: string
  status: string
  content: string
  word_count: number
  content_type: string
}

export interface ReaderNav {
  prev_chapter_no: number | null
  next_chapter_no: number | null
  total_chapters: number
}

export interface ReaderAssetImage {
  url: string
  caption?: string | null
  status?: string | null
}

export interface ReaderAssetAudio {
  url: string
  duration_sec?: number | null
  status?: string | null
}

export interface ReaderBundle {
  project: ReaderProject
  chapter: ReaderChapter
  nav: ReaderNav
  branch: ReaderBranch
  assets: {
    header_image: ReaderAssetImage | null
    audio: ReaderAssetAudio | null
    panels: unknown[]
    video: unknown[]
  }
  modalities: ReaderModalities
}

export interface ReaderExportFile {
  blob: Blob
  filename: string
}

function readerQuery(branchId?: string | null, extra?: Record<string, string>) {
  const qs = new URLSearchParams(extra)
  const branch = branchId?.trim() || getCurrentBranchId()
  if (branch) qs.set('branch', branch)
  const value = qs.toString()
  return value ? `?${value}` : ''
}

function contentDispositionFilename(header: string | null, fallback: string) {
  if (!header) return fallback
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ''))
  const quotedMatch = /filename="([^"]+)"/i.exec(header)
  if (quotedMatch?.[1]) return quotedMatch[1]
  const plainMatch = /filename=([^;]+)/i.exec(header)
  return plainMatch?.[1]?.trim() || fallback
}

async function exportProject(projectId: string, format: ReaderExportFormat, branchId?: string | null): Promise<ReaderExportFile> {
  const path = `/v1/projects/${encodeURIComponent(projectId)}/export${readerQuery(branchId, { format })}`
  return downloadExport(path, format)
}

async function exportChapter(
  projectId: string,
  chapterNo: number,
  format: ReaderExportFormat,
  branchId?: string | null,
): Promise<ReaderExportFile> {
  const path = `/v1/projects/${encodeURIComponent(projectId)}/export${readerQuery(branchId, {
    format,
    chapter: String(chapterNo),
  })}`
  return downloadExport(path, format)
}

async function exportVolume(
  projectId: string,
  volumeNo: number,
  format: ReaderExportFormat,
  branchId?: string | null,
): Promise<ReaderExportFile> {
  const path = `/v1/projects/${encodeURIComponent(projectId)}/export${readerQuery(branchId, {
    format,
    volume: String(volumeNo),
  })}`
  return downloadExport(path, format)
}

async function downloadExport(path: string, format: ReaderExportFormat): Promise<ReaderExportFile> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
  })
  if (!res.ok) {
    let message = res.statusText
    let code: string | undefined
    const data = await res.json().catch(() => undefined)
    if (data && typeof data === 'object') {
      const payload = data as Record<string, unknown>
      if (typeof payload.message === 'string') message = payload.message
      if (typeof payload.code === 'string') code = payload.code
      if (payload.error && typeof payload.error === 'object') {
        const err = payload.error as Record<string, unknown>
        if (typeof err.message === 'string') message = err.message
        if (typeof err.code === 'string') code = err.code
      } else if (typeof payload.error === 'string') {
        message = payload.error
      }
    }
    throw new ApiError(res.status, message, code)
  }
  const blob = await res.blob()
  return {
    blob,
    filename: contentDispositionFilename(
      res.headers.get('Content-Disposition'),
      `novel.${format === 'epub3' ? 'epub' : format}`,
    ),
  }
}

export const readerApi = {
  toc: (projectId: string, branchId?: string | null): Promise<ReaderToc> =>
    request<ReaderToc>(`/v1/projects/${encodeURIComponent(projectId)}/toc${readerQuery(branchId)}`),

  bundle: (projectId: string, chapterNo: number, branchId?: string | null): Promise<ReaderBundle> =>
    request<ReaderBundle>(
      `/v1/projects/${encodeURIComponent(projectId)}/chapters/${encodeURIComponent(String(chapterNo))}/bundle${readerQuery(branchId)}`,
    ),

  exportProject,
  exportChapter,
  exportVolume,
}
